/**
 * SignOutButton
 *
 * Shared sign-out control used across the onboarding app (sidebar,
 * post-submission screens). Wraps `useSignOut` from `@order-up/client/vite`.
 * The `AuthenticatedRoute` wrapping the app will redirect to the auth
 * service on the next render once Firebase is signed out.
 */

import { useAuth, useSignOut } from '@order-up/client/vite';
import { LogOut } from 'lucide-react';

export interface SignOutButtonProps {
  variant?: 'sidebar' | 'inline';
  className?: string;
}

const PROVIDER_LABELS: Record<string, string> = {
  'google.com': 'Google',
  'apple.com': 'Apple',
  'facebook.com': 'Facebook',
  password: 'Email',
};

/** Map Firebase providerId → friendly label, or null if no provider data. */
function getProviderLabel(providerId: string | undefined): string | null {
  if (!providerId) return null;
  return PROVIDER_LABELS[providerId] ?? providerId;
}

export function SignOutButton({ variant = 'sidebar', className = '' }: SignOutButtonProps) {
  const { mutate: signOut, isPending } = useSignOut();
  const { data: authData } = useAuth();
  const email = authData?.authUser?.email ?? '';
  const providerLabel = getProviderLabel(authData?.firebaseUser?.providerData?.[0]?.providerId);

  const captionTextSize = variant === 'inline' ? 'text-xs' : 'text-[10px]';
  const caption = email ? (
    <div className={`mt-2 ${captionTextSize} text-zinc-400 text-center leading-tight`}>
      {providerLabel && <div>Signed in with {providerLabel}</div>}
      <div className="truncate">{email}</div>
    </div>
  ) : null;

  if (variant === 'inline') {
    return (
      <div className={`flex flex-col items-center ${className}`}>
        <button
          type="button"
          onClick={() => signOut()}
          disabled={isPending}
          className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-zinc-900/50 px-4 py-2 pointer-coarse:min-h-11 text-sm font-medium text-zinc-300 hover:border-white/20 hover:bg-zinc-800 hover:text-white transition-colors disabled:opacity-50"
        >
          <LogOut className="h-4 w-4" aria-hidden />
          {isPending ? 'Signing out...' : 'Sign Out'}
        </button>
        {caption}
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`}>
      <button
        type="button"
        onClick={() => signOut()}
        disabled={isPending}
        className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-white/5 bg-zinc-900/30 px-3 py-2 pointer-coarse:min-h-11 text-xs font-medium text-zinc-400 hover:border-white/10 hover:bg-zinc-800/60 hover:text-white transition-colors disabled:opacity-50"
      >
        <LogOut className="h-3.5 w-3.5" aria-hidden />
        {isPending ? 'Signing out...' : 'Sign Out'}
      </button>
      {caption}
    </div>
  );
}
